import { defineStore } from 'pinia';
import { useUpdateStatusStore } from './updateStatus';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  createSubmissionAPI,
  createSubmissionEntryFormAPI,
  getSubmissionEntryFormAPI,
  getTeamCriteriaSelectionsAPI,
  removeOptionalCriteriaAPI,
  addOptionalCriteriaAPI,
  updateSubmissionEntryFormAPI,
  saveCriteriaSelectionUseAPI,
} from '@/services';
import {
  ISubmissionEntryFormDTO,
  ITeamCriteriaSelectionDTO,
  TeamCriteriaSelectionDTO,
} from '@/dtos';
import { computed, Ref } from 'vue';

export const useSubmissionnStore = defineStore('submission', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();

  const getSubmissionEntryQuery = (submissionId: Ref<string>) => {
    return useQuery({
      queryKey: ['submission-entry', submissionId.value],
      queryFn: async (): Promise<ISubmissionEntryFormDTO> => {
        const ret = await getSubmissionEntryFormAPI(submissionId.value);
        return ret;
      },
      enabled: computed(() => !!submissionId.value && submissionId.value != ''),
    });
  };

  const getCriteriaSelectionsQuery = (teamCriteriaId: Ref<string>) => {
    return useQuery({
      queryKey: ['my-criteria-selections'],
      queryFn: async (): Promise<{ data: ITeamCriteriaSelectionDTO[]; count: number }> => {
        const ret = await getTeamCriteriaSelectionsAPI(teamCriteriaId.value);
        const critera = ret.data.map((crit) => new TeamCriteriaSelectionDTO(crit));

        return { data: critera, count: ret.count };
      },
      enabled: computed(() => !!teamCriteriaId.value && teamCriteriaId.value != ''),
    });
  };

  // Mutations
  const create = useMutation({
    mutationFn: () => {
      return createSubmissionAPI();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`registration`, err);
    },
  });

  const createEntry = useMutation({
    mutationFn: (submissionId: string) => {
      return createSubmissionEntryFormAPI(submissionId);
    },
    onSuccess: (_data, submissionId) => {
      queryClient.invalidateQueries({ queryKey: ['submission-entry', submissionId] });
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`submission-entry`, err);
    },
  });

  const updateEntry = useMutation({
    mutationFn: ({
      submissionId,
      submissionEntryForm,
    }: {
      submissionId: string;
      submissionEntryForm: ISubmissionEntryFormDTO;
    }) => {
      return updateSubmissionEntryFormAPI(submissionId, submissionEntryForm);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['submission-entry', variables.submissionId] });
      statusStore.setSuccessStatus(`submission-entry-${variables.submissionId}`, 'success:update');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`submission-entry-${variables.submissionId}`, err);
    },
  });

  const removeOptionalCriteria = useMutation({
    mutationFn: (critId: string) => {
      return removeOptionalCriteriaAPI(critId);
    },
    onSuccess: (_data, critId) => {
      queryClient.invalidateQueries({ queryKey: ['my-criteria-selections'] });
      statusStore.setSuccessStatus(`criteria-selection-${critId}`, 'success:update');
    },
    onError: (err: string, critId) => {
      statusStore.setErrorStatus(`criteria-selection-${critId}`, err);
    },
  });

  const addOptionalCriteria = useMutation({
    mutationFn: (critIds: string[]) => {
      return addOptionalCriteriaAPI(critIds);
    },
    onSuccess: (_data, critId) => {
      queryClient.invalidateQueries({ queryKey: ['my-criteria-selections'] });
      statusStore.setSuccessStatus(`criteria-selection-${critId}`, 'success:update');
    },
    onError: (err: string, critId) => {
      statusStore.setErrorStatus(`criteria-selection-${critId}`, err);
    },
  });

  const updateCriteriaSelection = useMutation({
    mutationFn: (criteriaSelection: ITeamCriteriaSelectionDTO) => {
      return saveCriteriaSelectionUseAPI(criteriaSelection);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-criteria-selections'] });
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string, item) => {
      statusStore.setErrorStatus(`criteria-selection-${item}`, err);
    },
  });

  const invalidateMyCriteriaSelections = () => {
    queryClient.invalidateQueries({ queryKey: ['my-criteria-selections'] });
  };

  return {
    create,
    createEntry,
    updateEntry,
    removeOptionalCriteria,
    addOptionalCriteria,
    updateCriteriaSelection,
    getSubmissionEntryQuery,
    getCriteriaSelectionsQuery,
    invalidateMyCriteriaSelections,
  };
});
